import { UnreachableCaseError } from '../util/unreachableCaseError';
import { NlpQueryParseType, NlpQueryType } from './contracts';
import type {
    CompanySuggestionsResponseData,
    NlpAutocompleteRequestData,
    NlpAutocompleteResponseData,
    NlpExample,
    NlpExampleResponse,
    NlpParseRequestData,
    NlpParseResponseData as NlpParseResponseDataDeprecated,
    NlpSimilarRequestData,
    NlpSimilarResponseData,
} from './contracts';
import { mapNlpAPIError } from './error';
import type { NlpAPIError } from './error';
import {
    NlpLlmParseErrorResponseSchema,
    NlpLlmSimilarResponseSchema,
    NlpParseResponseDataSchema,
    NlpParseResponseErrorSchema,
} from './schema';
import type { NlpParseResponseData, NlpParseResponseError } from './schema';
import { AstNodeType } from '@thinkalpha/language-services';
import type { KnownAstNode } from '@thinkalpha/language-services';
import type {
    IndicatorImportViewModel,
    IndicatorViewModel,
} from '@thinkalpha/ui-types/contracts/dictionary-view-model';
import type { ResourceQueryResponseWithMeta } from '@thinkalpha/ui-types/contracts/resource-query';
import type { AxiosResponse } from 'axios';
import { memoize } from 'es-toolkit';
import { t } from 'i18next';
import { firstValueFrom, of, pipe, ReplaySubject } from 'rxjs';
import type { Observable, OperatorFunction } from 'rxjs';
import { catchError, map, share, switchMap } from 'rxjs/operators';
import { createInstance, createLegacyInstance } from 'src/api';
import { replaceFormulaNodes } from 'src/features/legacyIfThen/if-then-helpers';
import { appConfig } from 'src/lib/config';
import { createImportAlias } from 'src/lib/dictionary/imports-helpers';
import { rxifyAxios } from 'src/lib/http';
import { container } from 'src/StaticContainer';

const log = container.get('Logger').getSubLogger({ name: 'nlp-parsing' });

const axios = createLegacyInstance({
    baseURL: appConfig.nlpApi,
    validateStatus: (status) => (status >= 200 && status <= 299) || status == 422, // 422 Unprocessable Entity is "successful" here
});

const rxAxios = rxifyAxios(axios, mapNlpAPIError());

const MAX_DENIED_INDICATORS_SHOWN = 5;
const translateNlpText = (key: string, defaultValue: string, options?: Record<string, unknown>): string =>
    t(key as any, defaultValue, options as any) as string;

// LLM NLP: Axios instance targeting platform-ws API (authenticated, no legacy serialization)
// Keep 400 responses resolved so the parse path can normalize known LLM errors without relying on thrown axios errors.
const llmAxios = createInstance(undefined, {
    useLegacySerialization: false,
    validateStatus: (status) => (status >= 200 && status <= 299) || status === 400,
});

export function fetchSuggestions(input: string, clientId: string): Observable<NlpParseResponseDataDeprecated> {
    return rxAxios
        .post<NlpParseResponseDataDeprecated | NlpAPIError>('/v1/parse', {
            type: 'query',
            clientId,
            isConditional: true,
            input,
        })
        .pipe(
            map((x) => {
                if (x.status === 422) {
                    return (x.data as NlpAPIError).detail.similar;
                } else {
                    return x.data as NlpParseResponseDataDeprecated;
                }
            }),
        );
}

export function replaceAliasInFormula(formula: string, previousAlias: string, newAlias: string): string {
    return replaceFormulaNodes(formula, [], (node: KnownAstNode | null) => {
        if (node && node.type === AstNodeType.call && node.source === previousAlias) {
            return { ...node, source: newAlias };
        }

        return node;
    });
}

/**
 * A function that processes the aliases and formula recieved from the NLP API
 * with respect to an array of already in-use imports, ensuring that duplicate
 * imports are stripped, duplicate alias names are renamed, and if any
 * adjustments are made to the NLP API aliases, that each alias referenced
 * in the NLP formula is updated.
 *
 * @deprecated Implement in ImportsManagerModel
 *
 * @param input the NlpParseResponseData to process
 * @param existingImports an array of imports from the context where NLP is being used
 *
 * @returns a new NlpParseResponseData with processed formula and aliases properties
 */
export function stripImportDuplicatesFromNlpParseResult(
    input: NlpParseResponseDataDeprecated,
    existingImports: IndicatorImportViewModel[],
): NlpParseResponseDataDeprecated {
    if (!input.result.formula || !input.result.imports) return input;

    const { formula, imports } = input.result.imports.reduce(
        (previous, current) => {
            // If import already exists on the strategy but with a different alias,
            // replace alias in formula with already existing alias
            const importMatch = existingImports.find((previousImport) => {
                return previousImport.id === current.id && previousImport.alias !== current.alias;
            });

            if (importMatch) {
                return {
                    ...previous,
                    formula: replaceAliasInFormula(previous.formula, current.alias, importMatch.alias),
                };
            }

            // If alias already exists on the strategy but for a different import,
            // create a new alias before adding the new import, and replace the
            // old alias with the new alias in the formula
            const aliasMatch = existingImports.find((previousImport) => {
                return previousImport.alias === current.alias && previousImport.id !== current.id;
            });

            if (aliasMatch) {
                // Construct necessary Indicator properties for createImportAlias and cast as Indicator
                const alias = createImportAlias(
                    { symbol: current.alias, key: null } as IndicatorViewModel,
                    existingImports,
                );
                return {
                    formula: replaceAliasInFormula(previous.formula, current.alias, alias),
                    imports: [
                        ...previous.imports,
                        {
                            ...current,
                            alias,
                        },
                    ],
                };
            }

            // Else, no issues exist, so just add the new import
            return { ...previous, imports: [...previous.imports, current] };
        },
        { formula: input.result.formula, imports: [] },
    );

    return { ...input, result: { formula, imports } };
}

/**
 * @deprecated No rxjs
 */
export function getAutocompletePhrase(queryType: NlpQueryType): OperatorFunction<string, string | undefined> {
    return pipe(
        switchMap((input) => {
            if (!input) return of(undefined);

            const request: NlpAutocompleteRequestData = {
                key: 'ticker',
                input,
                isConditional: queryType === 'condition',
            };

            return rxAxios.post<NlpAutocompleteResponseData | NlpAPIError>('/v1/autocomplete', request).pipe(
                map(({ data }: AxiosResponse<NlpAutocompleteResponseData>) => {
                    return data.autocomplete.length ? data.autocomplete[0] : undefined;
                }),
                catchError((error: NlpAPIError | Error) => {
                    log.debug({
                        message: `NLP /autocomplete encountered an error parsing ${input}. ${error}`,
                    });
                    return of(undefined);
                }),
            );
        }),
    );
}

export async function getSimilarQueriesP(
    req: NlpSimilarRequestData,
    signal?: AbortSignal,
    useLlm?: boolean,
): Promise<NlpSimilarResponseData> {
    if (useLlm) {
        if (TRACE) {
            log.trace({
                name: 'getSimilarQueriesP',
                message: 'going to LLM endpoint for similar queries',
                input: req.input,
            });
        }

        try {
            const res = await llmAxios.post('/nlp/similar', { input: req.input }, { signal });
            const validated = NlpLlmSimilarResponseSchema.safeParse(res.data);

            if (!validated.success) {
                log.error({
                    message: 'Failed to validate LLM similar queries response',
                    input: req.input,
                    error: validated.error,
                });

                return { input: req.input, similar: [] };
            }

            return { input: req.input, similar: validated.data.similar };
        } catch (e: unknown) {
            if (e && typeof e === 'object' && 'code' in e && (e as { code?: string }).code === 'ERR_CANCELED') {
                throw e;
            }

            if (e instanceof Error) {
                log.error({
                    message: 'Failed to fetch similar queries from LLM endpoint',
                    input: req.input,
                    error: e,
                });
            }

            return { input: req.input, similar: [] };
        }
    }

    const res = await axios.post('/v1/similar', req, { signal });

    return res.data;
}

/**
 * @deprecated No rxjs
 */
export function getSimilarQueries(queryType: NlpQueryType): OperatorFunction<string, NlpSimilarResponseData> {
    return pipe(
        switchMap((input) => {
            if (!input) return of({ similar: [], input });

            const key = 'ticker';
            const isConditional = queryType === 'condition';
            const request: NlpSimilarRequestData = { key, input, isConditional };

            return rxAxios.post<NlpSimilarResponseData | NlpAPIError>('/v1/similar', request).pipe(
                map((x) => {
                    const success = x.data as NlpSimilarResponseData;
                    return { ...success, input };
                }),
                catchError((error: NlpAPIError | Error) => {
                    log.debug({
                        message: `NLP /parse encountered an error parsing ${input}. ${error}`,
                    });
                    return of({ similar: [], input });
                }),
            );
        }),
    );
}

export async function parseNaturalLanguageP(
    input: string,
    queryType: NlpQueryType,
    clientId: string,
    signal?: AbortSignal,
    useLlm?: boolean,
    backtest?: boolean,
): Promise<NlpParseResponseData | NlpParseResponseError> {
    const key = 'ticker';
    const type = queryType === 'date' ? NlpQueryParseType.date : NlpQueryParseType.query;
    const isConditional = queryType === 'condition';

    // when all we have is a number, we don't need to make a network call
    if (!isNaN(Number(input))) {
        if (TRACE) {
            log.trace({
                name: 'parseNaturalLanguageP',
                message: 'Input is a number',
                input,
            });
        }
        return {
            docId: '',
            modified: null,
            key,
            result: { formula: input, imports: [] },
            similar: [],
            namedColumns: [],
            benchmarkFormulae: [],
        };
    }

    if (useLlm) {
        return parseNaturalLanguageLlm(input, type, isConditional, signal, backtest);
    }

    if (TRACE) {
        log.trace({
            name: 'parseNaturalLanguageP',
            message: 'going to network to parse input',
            input,
        });
    }

    const request: NlpParseRequestData = {
        type,
        key,
        input,
        isConditional,
        clientId,
        isDone: true,
    };

    const res = await axios.post('/v1/parse', request, { signal });

    if (res.status === 422) {
        try {
            const response = NlpParseResponseErrorSchema.parse(res.data);
            return response;
        } catch (e: unknown) {
            if (e instanceof Error) {
                log.error({ message: 'Failed to parse NLP response', error: e });
            }
            throw e;
        }
    } else {
        try {
            const response = NlpParseResponseDataSchema.parse(res.data);
            return response;
        } catch (e: unknown) {
            if (e instanceof Error) {
                log.error({ message: 'Failed to parse NLP response', error: e });
            }
            throw e;
        }
    }
}

/**
 * Parse natural language via the LLM-powered platform-ws endpoint.
 * Normalizes the response to match the legacy NLP shape so consumers don't need to change.
 */
async function parseNaturalLanguageLlm(
    input: string,
    type: NlpQueryParseType,
    isConditional: boolean,
    signal?: AbortSignal,
    backtest?: boolean,
): Promise<NlpParseResponseData | NlpParseResponseError> {
    if (TRACE) {
        log.trace({
            name: 'parseNaturalLanguageLlm',
            message: 'going to LLM endpoint to parse input',
            input,
        });
    }

    try {
        const res = await llmAxios.post(
            '/nlp/parse',
            { type, input, isConditional, backtest: backtest ?? false },
            { signal },
        );

        if (res.status === 400) {
            const errorParse = NlpLlmParseErrorResponseSchema.safeParse(res.data);
            if (errorParse.success) {
                const { error, errorCode, deniedIndicators } = errorParse.data;
                const detailMessage =
                    errorCode === 'ENTITLEMENT_DENIED'
                        ? deniedIndicators?.length
                            ? translateNlpText(
                                  'widgets::searchalpha::nlp::entitlement-denied-with-indicators',
                                  'This query uses indicators your account cannot access: {{indicators}}{{suffix}}',
                                  {
                                      indicators: deniedIndicators.slice(0, MAX_DENIED_INDICATORS_SHOWN).join(', '),
                                      suffix: deniedIndicators.length > MAX_DENIED_INDICATORS_SHOWN ? ', ...' : '.',
                                  },
                              )
                            : translateNlpText(
                                  'widgets::searchalpha::nlp::entitlement-denied',
                                  'This query uses indicators your account cannot access.',
                              )
                        : error;
                // Normalize LLM error to legacy NLP error shape
                return {
                    detail: {
                        msg: detailMessage,
                        similar: [],
                    },
                };
            }
        }

        const response = NlpParseResponseDataSchema.parse(res.data);
        return response;
    } catch (e: unknown) {
        // User-initiated cancellation — propagate without logging
        if (e && typeof e === 'object' && 'code' in e && (e as { code?: string }).code === 'ERR_CANCELED') {
            throw e;
        }

        if (e instanceof Error) {
            log.error({ message: 'Failed to parse LLM NLP response', error: e });
        }
        throw e;
    }
}

/**
 * @deprecated No rxjs
 */
async function parseNaturalLanguage(
    input: string,
    queryType: NlpQueryType,
    clientId: string,
): Promise<NlpParseResponseDataDeprecated> {
    const key = 'ticker';
    const type = queryType === 'date' ? NlpQueryParseType.date : NlpQueryParseType.query;
    const isConditional = queryType === 'condition';

    const genericResponseData: NlpParseResponseDataDeprecated = {
        docId: '',
        similar: [],
        modified: null,
        key,
        clientId,
        result: { formula: '', imports: [] },
        namedColumns: [],
        benchmarkFormulae: [],
    };

    const request: NlpParseRequestData = {
        type,
        key,
        input,
        isConditional,
        clientId,
        isDone: true,
    };

    return await firstValueFrom(
        rxAxios.post<NlpParseResponseDataDeprecated | NlpAPIError>('/v1/parse', request).pipe(
            map((x) => {
                if (x.status === 422) {
                    // couldn't process the user input, so there's instead suggestions
                    const err = x.data as NlpAPIError;
                    return { ...genericResponseData, similar: err.detail.similar };
                } else {
                    const success = x.data as NlpParseResponseDataDeprecated;
                    return { ...success, input };
                }
            }),
            catchError((error: NlpAPIError | Error) => {
                log.debug({
                    message: `NLP /parse encountered an error parsing ${input}. ${error}`,
                });
                return of({ ...genericResponseData, similar: [] });
            }),
        ),
    );
}

/**
 * @deprecated No rxjs
 */
export const parseNaturalLanguageMemo = memoize(parseNaturalLanguage);

/**
 * @deprecated No rxjs
 */
export const commonNlpExamples$: Observable<NlpExampleResponse> = rxAxios
    .get<NlpExampleResponse>('/v1/examples', {
        params: { shortlist: true, count: 1000, caseType: 'all_examples' },
    })
    .pipe(
        map((x) => x.data),
        share({
            connector: () => new ReplaySubject(1),
        }),
    );

export const getNlpExamplesQuery = async (
    type: NlpQueryType,
): Promise<ResourceQueryResponseWithMeta<{ id: string; input: string; formula: string }>> => {
    const { data, count } = (
        await axios.get<NlpExampleResponse>('/v1/examples', {
            params: {
                shortlist: false,
                count: 100000,
                page: 1,
                caseType: mapTypeToContract(type),
            },
        })
    ).data;

    return {
        results: data.map((x) => ({
            id: x.input,
            input: x.input,
            formula: x.formula,
        })),
        count,
    };
};

export function mapTypeToContract(type: NlpQueryType): NlpExample['type'] {
    switch (type) {
        case NlpQueryType.operand:
            return 'operands';
        case NlpQueryType.date:
            return 'timeframes';
        case NlpQueryType.condition:
            return 'conditions';
        default:
            throw new UnreachableCaseError(type);
    }
}

export async function getCompanySuggestions(query: string) {
    return (
        await axios.post<CompanySuggestionsResponseData>('/v1/companies', {
            input: query,
        })
    ).data;
}
